/**
 * TeamSystem Impact - Sidebar Navigation
 * Con supporto per sottomenu DMA e selezione anno
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Avatar,
  Badge,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Toolbar,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Collapse,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assessment as AssessmentIcon,
  DataUsage as DataUsageIcon,
  Description as DescriptionIcon,
  Nature as EcoIcon,
  Settings as SettingsIcon,
  History as HistoryIcon,
  Forum as ForumIcon,
  Folder as FolderIcon,
  VpnKey as VpnKeyIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandLess,
  ExpandMore,
  ListAlt as CatalogIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  Balance as BalanceIcon,
  Inbox as InboxIcon,
  Language as LanguageIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  SettingsBrightness as SettingsBrightnessIcon,
  BusinessCenter as BusinessCenterIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../../store/authStore';
import { useNotificationStore } from '../../../store/notificationStore';
import { useWorkingYear } from '../../../store/workingYearStore';
import { useThemeStore } from '../../../store/themeStore';
import type { ThemeMode } from '../../../store/themeStore';
import { useUserAssignments } from '../../../modules/data-capture/hooks/useUserAssignments';
import { useUserDmaAssignments } from '../../../modules/dma/hooks/useUserDmaAssignments';
import { useMyDeals } from '../../../modules/ma/useMyDeals';
import { isReadOnlyRole } from '../../../utils/permissions';

interface MenuItemConfig {
  id: string;
  labelKey: string;
  path: string;
  icon: React.ReactNode;
  badge?: boolean;
  children?: MenuItemConfig[];
}

interface SidebarProps {
  drawerWidth: number;
  mobileOpen: boolean;
  onMobileClose: () => void;
  desktopCollapsed?: boolean;
  onDesktopToggle?: () => void;
}

const menuItems: MenuItemConfig[] = [
  {
    id: 'dashboard',
    labelKey: 'navigation.dashboard',
    path: '/dashboard',
    icon: <DashboardIcon />,
  },
  {
    id: 'inbox',
    labelKey: 'navigation.inbox',
    path: '/inbox',
    icon: <InboxIcon />,
    badge: true,
  },
  {
    id: 'engagement',
    labelKey: 'navigation.engagement',
    path: '/engagement',
    icon: <ForumIcon />,
  },
  {
    // Modulo riservato: la voce compare SOLO a chi è autorizzato ad almeno un
    // fascicolo M&A (vedi il filtro su hasDeals più sotto). Per tutti gli altri
    // il modulo non deve nemmeno risultare esistente.
    id: 'ma',
    labelKey: 'navigation.ma',
    path: '/ma',
    icon: <BusinessCenterIcon />,
  },
  {
    id: 'dma',
    labelKey: 'navigation.dma',
    path: '/dma',
    icon: <AssessmentIcon />,
    children: [
      {
        id: 'dma-catalog',
        labelKey: 'navigation.dma_catalog',
        path: '/dma/catalog',
        icon: <CatalogIcon />,
      },
      {
        id: 'dma-assessment',
        labelKey: 'navigation.dma_assessment',
        path: '/dma/assessment',
        icon: <PeopleIcon />,
      },
      {
        id: 'dma-materiality',
        labelKey: 'navigation.dma_materiality',
        path: '/dma/materiality',
        icon: <BalanceIcon />,
      },
      {
        id: 'dma-report',
        labelKey: 'navigation.dma_report',
        path: '/reporting/overview',
        icon: <DescriptionIcon />,
      },
    ],
  },
  {
    id: 'data',
    labelKey: 'navigation.data',
    path: '/data',
    icon: <DataUsageIcon />,
    children: [
      {
        id: 'data-configuration',
        labelKey: 'navigation.data_configuration',
        path: '/data/configuration',
        icon: <SettingsIcon />,
      },
      {
        id: 'data-collection',
        labelKey: 'navigation.data_collection',
        path: '/data/collection',
        icon: <DataUsageIcon />,
      },
      {
        id: 'data-validation',
        labelKey: 'navigation.data_validation',
        path: '/data/validation',
        icon: <CatalogIcon />,
      },
      {
        id: 'data-roles',
        labelKey: 'navigation.data_roles',
        path: '/data/roles',
        icon: <PeopleIcon />,
      },
    ],
  },
  {
    id: 'taxonomy',
    labelKey: 'navigation.taxonomy',
    path: '/taxonomy',
    icon: <EcoIcon />,
  },
  {
    id: 'audit',
    labelKey: 'navigation.audit',
    path: '/audit',
    icon: <HistoryIcon />,
  },
];

const settingsItem: MenuItemConfig = {
  id: 'settings',
  labelKey: 'navigation.settings',
  path: '/settings',
  icon: <SettingsIcon />,
  children: [
    {
      id: 'settings-info',
      labelKey: 'navigation.settings_info',
      path: '/settings/info',
      icon: <SettingsIcon />,
    },
    {
      id: 'settings-year',
      labelKey: 'navigation.settings_year_management',
      path: '/settings/year-management',
      icon: <CalendarIcon />,
    },
    {
      id: 'settings-users',
      labelKey: 'navigation.settings_users',
      path: '/settings/users',
      icon: <PeopleIcon />,
    },
    {
      id: 'settings-connections',
      labelKey: 'navigation.settings_connections',
      path: '/settings/connections',
      icon: <VpnKeyIcon />,
    },
    {
      id: 'settings-sustainability',
      labelKey: 'navigation.settings_sustainability',
      path: '/settings/sustainability',
      icon: <EcoIcon />,
    },
  ],
};

// Re-export useWorkingYear for backward compatibility
export { useWorkingYear as useDMAYear } from '../../../store/workingYearStore';

export const Sidebar = ({
  drawerWidth,
  mobileOpen,
  onMobileClose,
  desktopCollapsed = false,
  onDesktopToggle,
}: SidebarProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, utente } = useAuthStore();
  const { instance } = useMsal();
  const { unreadCount } = useNotificationStore();
  const { year: workingYear, setYear: setWorkingYear, availableYears } = useWorkingYear();
  const { mode: themeMode, setMode: setThemeMode } = useThemeStore();
  const { hasOwnerAssignments, hasSupervisorAssignments } = useUserAssignments();
  const { hasDmaAssessments } = useUserDmaAssignments();
  const { hasDeals: hasMaDeals } = useMyDeals();

  const ruolo = utente?.ruolo;
  const isAdminOrEsg = ruolo === 'amministratore' || ruolo === 'responsabile_esg';
  const isContributore = ruolo === 'contributore';
  // Ruolo di sola lettura: vede tutto (DMA + Raccolta) in lettura, senza scrivere
  const isReadOnly = isReadOnlyRole(ruolo);

  const dataChildren: MenuItemConfig[] = [];
  if (isAdminOrEsg) {
    dataChildren.push({ id: 'data-configuration', labelKey: 'navigation.data_configuration', path: '/data/configuration', icon: <SettingsIcon /> });
  }
  // Il visualizzatore vede la Raccolta e la Validazione in sola lettura;
  // il contributore solo se assegnato come owner/supervisore.
  if (isAdminOrEsg || isReadOnly || (isContributore && hasOwnerAssignments)) {
    dataChildren.push({ id: 'data-collection', labelKey: 'navigation.data_collection', path: '/data/collection', icon: <DataUsageIcon /> });
  }
  if (isAdminOrEsg || isReadOnly || (isContributore && hasSupervisorAssignments)) {
    dataChildren.push({ id: 'data-validation', labelKey: 'navigation.data_validation', path: '/data/validation', icon: <CatalogIcon /> });
  }
  // Report assessment: anche il visualizzatore, in sola lettura
  if (isAdminOrEsg || ruolo === 'visualizzatore') {
    dataChildren.push({ id: 'data-report', labelKey: 'navigation.data_report_assessment', path: '/data/report', icon: <DescriptionIcon /> });
  }
  if (isAdminOrEsg) {
    dataChildren.push({ id: 'data-roles', labelKey: 'navigation.data_roles', path: '/data/roles', icon: <PeopleIcon /> });
  }
  // Fonte dei dati: libreria documenti, visibile a chiunque veda la sezione Dati
  if (dataChildren.length > 0) {
    dataChildren.push({ id: 'data-documents', labelKey: 'navigation.data_documents', path: '/data/documents', icon: <FolderIcon /> });
  }

  // DMA per contributore: solo "Valutazioni" se ha IRO assegnati come stakeholder; altrimenti nascosto
  const dmaAllChildren = menuItems.find((item) => item.id === 'dma')?.children ?? [];
  const dmaChildren = isContributore
    ? dmaAllChildren.filter((child) => child.id === 'dma-assessment')
    : dmaAllChildren;

  const computedMenuItems = menuItems
    .map((item) => {
      if (item.id === 'data') return { ...item, children: dataChildren };
      if (item.id === 'dma') return { ...item, children: dmaChildren };
      return item;
    })
    .filter((item) => {
      // Nascondi i top-level non pertinenti al contributore
      if (isContributore) {
        if (item.id === 'taxonomy' || item.id === 'audit' || item.id === 'engagement') return false;
        if (item.id === 'dma' && !hasDmaAssessments) return false;
      }
      // Nascondi il top-level "data" se non ha sotto-voci visibili
      if (item.id === 'data' && dataChildren.length === 0) return false;
      // M&A: visibile solo a chi è autorizzato ad almeno un fascicolo. Vale per
      // ogni ruolo, amministratori compresi: qui comanda l'ACL, non il ruolo.
      if (item.id === 'ma' && !hasMaDeals) return false;
      return true;
    });

  // Stato per espansione sottomenu
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({
    dma: location.pathname.startsWith('/dma'),
    data: location.pathname.startsWith('/data'),
    settings: location.pathname.startsWith('/settings'),
  });

  // Aggiorna espansione quando cambia la location
  useEffect(() => {
    if (location.pathname.startsWith('/dma')) {
      setExpandedMenus(prev => ({ ...prev, dma: true }));
    }
    if (location.pathname.startsWith('/data')) {
      setExpandedMenus(prev => ({ ...prev, data: true }));
    }
    if (location.pathname.startsWith('/settings')) {
      setExpandedMenus(prev => ({ ...prev, settings: true }));
    }
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/dma' || path === '/data' || path === '/settings') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    onMobileClose();
  };

  const handleToggleMenu = (menuId: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId],
    }));
  };

  // User popover menu
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  const handleUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    handleUserMenuClose();
  };

  const handleThemeChange = (mode: ThemeMode) => {
    setThemeMode(mode);
    handleUserMenuClose();
  };

  const handleLogout = async () => {
    const account = instance.getActiveAccount();
    if (account) {
      try {
        // Usa un timeout per evitare blocchi dovuti a COOP headers
        await Promise.race([
          instance.logoutPopup({ account, postLogoutRedirectUri: '/login' }),
          new Promise<void>((resolve) => setTimeout(resolve, 4000)),
        ]);
      } catch {
        // Se il popup fallisce, procedi comunque con il logout locale
      }
    }
    logout();
    navigate('/login');
    onMobileClose();
  };

  const renderMenuItem = (item: MenuItemConfig, isChild = false) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus[item.id];
    const active = isActive(item.path);

    return (
      <Box key={item.id}>
        <ListItem disablePadding sx={{ px: desktopCollapsed ? 0.5 : 1.5 }}>
          <Tooltip
            title={desktopCollapsed ? t(item.labelKey) : ''}
            placement="right"
            arrow
            disableInteractive
          >
            <ListItemButton
              component={hasChildren ? 'div' : 'a'}
              href={hasChildren ? undefined : item.path}
              selected={active && !hasChildren}
              onClick={(e: React.MouseEvent) => {
                if (hasChildren && !desktopCollapsed) {
                  e.preventDefault();
                  e.stopPropagation();
                  handleToggleMenu(item.id);
                } else {
                  // Let browser handle Ctrl+Click / middle-click natively (opens in new tab)
                  if (e.ctrlKey || e.metaKey || e.button === 1) return;
                  e.preventDefault();
                  e.stopPropagation();
                  handleNavigation(item.path);
                }
              }}
              sx={{
                borderRadius: 1.5,
                mb: 0.25,
                py: 0.75,
                pl: isChild ? 3 : 1.5,
                pr: 1,
                width: '100%',
                justifyContent: desktopCollapsed ? 'center' : 'flex-start',
                textDecoration: 'none',
                color: 'var(--muted-foreground)',
                '&:hover': {
                  backgroundColor: 'var(--accent)',
                  color: 'var(--accent-foreground)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'var(--accent)',
                  color: 'var(--foreground)',
                  '&:hover': {
                    backgroundColor: 'var(--accent)',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'var(--primary)',
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: desktopCollapsed ? 'unset' : 32,
                  color: active ? 'var(--primary)' : 'var(--muted-foreground)',
                }}
              >
                {item.badge && unreadCount > 0 ? (
                  <Badge badgeContent={unreadCount} color="error" max={99}>
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              {!desktopCollapsed && (
                <>
                  <ListItemText
                    primary={t(item.labelKey)}
                    primaryTypographyProps={{
                      fontSize: isChild ? '0.75rem' : '0.8125rem',
                      fontWeight: active ? 600 : 400,
                    }}
                  />
                  {hasChildren && (
                    isExpanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />
                  )}
                </>
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>


        {/* Sottomenu */}
        {hasChildren && !desktopCollapsed && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map(child => renderMenuItem(child, true))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo */}
      <Toolbar sx={{ px: desktopCollapsed ? 1 : 3, py: 2, justifyContent: desktopCollapsed ? 'center' : 'space-between' }}>
        {!desktopCollapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <img
              src="/logo-teamsystem.png"
              alt="TeamSystem"
              style={{ width: 36, height: 36, borderRadius: 8 }}
            />
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'var(--foreground)', lineHeight: 1.2 }}>
                TeamSystem
              </Typography>
              <Typography variant="caption" sx={{ color: 'var(--muted-foreground)' }}>
                Impact
              </Typography>
            </Box>
          </Box>
        )}
        {desktopCollapsed && (
          <img
            src="/logo-teamsystem.png"
            alt="TeamSystem"
            style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'contain', flexShrink: 0 }}
          />
        )}
        {/* Toggle button - solo su desktop */}
        {onDesktopToggle && (
          <IconButton
            onClick={onDesktopToggle}
            size="small"
            aria-label={desktopCollapsed ? "Espandi barra laterale" : "Comprimi barra laterale"}
            sx={{
              display: { xs: 'none', md: 'flex' },
              color: 'var(--muted-foreground)',
              '&:hover': {
                backgroundColor: 'var(--accent)',
              },
            }}
          >
            {desktopCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        )}
      </Toolbar>

      <Divider sx={{ borderColor: 'var(--border)' }} />

      {/* Global Year Selector */}
      {!desktopCollapsed && (
        <Box sx={{ px: 3, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CalendarIcon sx={{ fontSize: 16, color: 'var(--muted-foreground)' }} />
            <Typography variant="caption" sx={{ color: 'var(--muted-foreground)' }}>
              Anno di lavoro
            </Typography>
          </Box>
          <FormControl fullWidth size="small">
            <Select
              value={workingYear}
              onChange={(e) => setWorkingYear(e.target.value as number)}
              sx={{
                color: 'var(--foreground)',
                fontSize: '0.875rem',
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: 'var(--border)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'var(--ring)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'var(--primary)',
                },
                '.MuiSvgIcon-root': {
                  color: 'var(--muted-foreground)',
                },
              }}
            >
              {availableYears.map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <Divider sx={{ borderColor: 'var(--border)' }} />

      {/* Main menu */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 1,
          // Hide scrollbar but keep scrolling
          scrollbarWidth: 'none', // Firefox
          '&::-webkit-scrollbar': { display: 'none' }, // Chrome, Safari
          msOverflowStyle: 'none', // IE
        }}
      >
        <List dense sx={{ py: 0 }}>
          {computedMenuItems.map((item) => renderMenuItem(item))}
        </List>
      </Box>

      {/* Bottom section: Search + User */}
      <Box sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
        <Divider sx={{ borderColor: 'var(--border)', mb: 1 }} />

        {/* Settings — solo admin / responsabile ESG */}
        {isAdminOrEsg && renderMenuItem(settingsItem)}

        {/* User button */}
        <Tooltip title={desktopCollapsed ? `${utente?.nome || ''} ${utente?.cognome || ''}` : ''} placement="right">
          <Box
            onClick={handleUserMenu}
            onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleUserMenu(e as any); } }}
            role="button"
            tabIndex={0}
            aria-label="Menu utente"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 1.5,
              py: 1,
              borderRadius: 1.5,
              cursor: 'pointer',
              justifyContent: desktopCollapsed ? 'center' : 'flex-start',
              '&:hover': { backgroundColor: 'var(--accent)' },
            }}
          >
            <Avatar
              sx={{
                width: 28,
                height: 28,
                bgcolor: 'var(--primary)',
                color: 'var(--primary-foreground)',
                fontSize: '0.7rem',
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {utente ? `${utente.nome[0]}${utente.cognome[0]}`.toUpperCase() : '?'}
            </Avatar>
            {!desktopCollapsed && (
              <Box sx={{ overflow: 'hidden' }}>
                <Typography variant="body2" fontWeight={500} noWrap sx={{ color: 'var(--foreground)', fontSize: '0.8125rem' }}>
                  {utente?.nome} {utente?.cognome}
                </Typography>
                <Typography variant="caption" noWrap sx={{ color: 'var(--muted-foreground)', display: 'block' }}>
                  {utente?.email}
                </Typography>
              </Box>
            )}
          </Box>
        </Tooltip>

        {/* User popover menu */}
        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={handleUserMenuClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          slotProps={{ paper: { sx: { minWidth: 200, ml: 1 } } }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {utente?.nome} {utente?.cognome}
            </Typography>
            <Typography variant="caption" sx={{ color: 'var(--muted-foreground)' }}>
              {utente?.email}
            </Typography>
          </Box>
          <Divider />
          <MenuItem onClick={() => handleLanguageChange('it')} selected={i18n.language === 'it'}>
            <ListItemIcon><LanguageIcon fontSize="small" /></ListItemIcon>
            Italiano
          </MenuItem>
          <MenuItem onClick={() => handleLanguageChange('en')} selected={i18n.language === 'en'}>
            <ListItemIcon><LanguageIcon fontSize="small" /></ListItemIcon>
            English
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => handleThemeChange('light')} selected={themeMode === 'light'}>
            <ListItemIcon><LightModeIcon fontSize="small" /></ListItemIcon>
            Light
          </MenuItem>
          <MenuItem onClick={() => handleThemeChange('dark')} selected={themeMode === 'dark'}>
            <ListItemIcon><DarkModeIcon fontSize="small" /></ListItemIcon>
            Dark
          </MenuItem>
          <MenuItem onClick={() => handleThemeChange('system')} selected={themeMode === 'system'}>
            <ListItemIcon><SettingsBrightnessIcon fontSize="small" /></ListItemIcon>
            System
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout} sx={{ color: 'var(--destructive)' }}>
            <ListItemIcon sx={{ color: 'var(--destructive)' }}>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            {t('auth.logout')}
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            transition: 'width 0.3s',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </>
  );
};
